import React, { createContext, useContext, useState, useEffect } from 'react';
import alertify from 'alertifyjs';

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [orders, setOrders] = useState([]);
  
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  const [favorites, setFavorites] = useState(() => {
    const savedFavorites = localStorage.getItem('favorites');
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });
  
  const [recentlyAdded, setRecentlyAdded] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Persist Cart
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Persist Favorites
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/store/categories/`);
      if (res.ok) {
        const data = await res.json();
        const categoryList = Array.isArray(data) ? data : (data.results || []);
        setCategories(categoryList);
      }
    } catch (e) {
      console.error("Failed to fetch categories", e);
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/store/regions/`);
      if (res.ok) {
        const data = await res.json();
        const regionList = Array.isArray(data) ? data : (data.results || []);
        setRegions(regionList);
      }
    } catch (e) {
      console.error("Failed to fetch regions", e);
    }
  };

  const fetchProducts = async (categoryId = null) => {
    setIsLoading(true);
    try {
      let url = `${API_BASE}/api/store/products/`;
      if (categoryId) {
        url += `?category=${categoryId}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const productList = Array.isArray(data) ? data : (data.results || []);
        // Since favorites are stored locally, we map the isFavorite flag
        const mappedProducts = productList.map(p => ({
          ...p,
          isFavorite: favorites.includes(p.id)
        }));
        setProducts(mappedProducts);
      }
    } catch (e) {
      console.error("Failed to fetch products", e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async () => {
    const token = localStorage.getItem('emba_access');
    if (!token) return;
    setIsOrdersLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/store/orders/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        const orderList = Array.isArray(data) ? data : (data.results || []);
        setOrders(orderList);
      }
    } catch (e) {
      console.error("Failed to fetch orders", e);
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const fetchCart = async () => {
    const token = localStorage.getItem('emba_access');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/store/cart/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.items) {
          const formattedItems = data.items.map(item => ({
            ...item,
            product: item.product_detail || item.product
          }));
          setCart(formattedItems);
        }
      }
    } catch (e) {
      console.error("Failed to fetch cart", e);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  // Sync products favorite status when favorites array changes
  useEffect(() => {
    setProducts(prev => {
      if (!Array.isArray(prev)) return [];
      return prev.map(p => ({
        ...p,
        isFavorite: favorites.includes(p.id)
      }));
    });
  }, [favorites]);

  const toggleFavorite = (productId) => {
    setFavorites(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const addToCart = async (product, quantity = 1, variantId = null) => {
    const token = localStorage.getItem('emba_access');
    
    // Find the variant detail if any
    const variantDetail = product.variants?.find(v => String(v.id) === String(variantId)) || null;

    if (token) {
      try {
        const payload = { product_id: product.id, quantity };
        if (variantId) payload.variant_id = variantId;

        const res = await fetch(`${API_BASE}/api/store/cart/add_item/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const data = await res.json();
          const formattedItems = (data.items || []).map(item => ({
            ...item,
            product: item.product_detail || item.product,
            variant_detail: item.variant_detail
          }));
          setCart(formattedItems);
        }
      } catch(e) { console.error(e); }
    } else {
      setCart(prev => {
        const existing = prev.find(item => item.product.id === product.id && item.variant_detail?.id === variantId);
        if (existing) {
          return prev.map(item => (item.product.id === product.id && item.variant_detail?.id === variantId) 
            ? { ...item, quantity: item.quantity + quantity } 
            : item);
        }
        return [...prev, { product, quantity, variant_detail: variantDetail }];
      });
    }
    const newItem = { product, quantity, variant_detail: variantDetail };
    setRecentlyAdded(newItem);
  };

  const removeFromCart = async (productId, variantId = null) => {
    const token = localStorage.getItem('emba_access');
    if (token) {
      try {
        const payload = { product_id: productId };
        if (variantId) payload.variant_id = variantId;

        const res = await fetch(`${API_BASE}/api/store/cart/remove_item/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const data = await res.json();
          const formattedItems = (data.items || []).map(item => ({
            ...item,
            product: item.product_detail || item.product,
            variant_detail: item.variant_detail
          }));
          setCart(formattedItems);
        }
      } catch(e) { console.error(e); }
    } else {
      setCart(prev => prev.filter(item => !(item.product.id === productId && item.variant_detail?.id === variantId)));
    }
    alertify.message('Removed from cart');
  };

  const updateCartQuantity = async (productId, quantity, variantId = null) => {
    if (quantity <= 0) {
      await removeFromCart(productId, variantId);
      return;
    }
    const token = localStorage.getItem('emba_access');
    if (token) {
      try {
        const payload = { product_id: productId, quantity: quantity };
        if (variantId) payload.variant_id = variantId;

        const res = await fetch(`${API_BASE}/api/store/cart/update_item/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const data = await res.json();
          const formattedItems = (data.items || []).map(item => ({
            ...item,
            product: item.product_detail || item.product,
            variant_detail: item.variant_detail
          }));
          setCart(formattedItems);
          return; // Successfully updated from backend
        }
      } catch(e) { console.error(e); }
    }
    // Fallback or unauthenticated local update
    setCart(prev => prev.map(item => (item.product.id === productId && item.variant_detail?.id === variantId) ? { ...item, quantity } : item));
  };

  const clearCart = () => {
    // If the order is created, the backend clears it for us, so we just clear local state.
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.variant_detail ? parseFloat(item.variant_detail.price) : parseFloat(item.product.price);
      return total + (price * item.quantity);
    }, 0);
  };

  return (
    <StoreContext.Provider value={{
        products, categories, regions, orders, cart, favorites,
        isLoading, isOrdersLoading,
        fetchProducts, fetchCategories, fetchRegions, fetchOrders, fetchCart, toggleFavorite,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      getCartTotal,
      recentlyAdded,
      setRecentlyAdded,
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
